import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";

const SESSION_KEY = "wa_impersonation";

export type ImpersonationKind = "admin" | "test";

interface ImpersonationState {
  userId: string;
  displayName: string;
  kind: ImpersonationKind;
  returnTo?: string;
  /** yyyy-MM-dd the test record is simulated as of. Display only; the backend reads it from the record. */
  simulatedDate?: string;
}

interface ImpersonationContextValue {
  impersonatedUserId: string | null;
  impersonatedDisplayName: string | null;
  impersonationKind: ImpersonationKind | null;
  impersonationReturnTo: string | null;
  impersonationSimulatedDate: string | null;
  startImpersonation: (userId: string, displayName: string) => void;
  startTestSession: (
    userId: string,
    displayName: string,
    returnTo: string,
    simulatedDate?: string | null,
  ) => void;
  stopImpersonation: () => void;
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextValue>({
  impersonatedUserId: null,
  impersonatedDisplayName: null,
  impersonationKind: null,
  impersonationReturnTo: null,
  impersonationSimulatedDate: null,
  startImpersonation: () => {},
  startTestSession: () => {},
  stopImpersonation: () => {},
  isImpersonating: false,
});

function loadFromSession(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ImpersonationState>;
    if (!parsed.userId || !parsed.displayName) return null;
    return {
      userId: parsed.userId,
      displayName: parsed.displayName,
      kind: parsed.kind === "test" ? "test" : "admin",
      returnTo: parsed.returnTo,
      simulatedDate: parsed.simulatedDate,
    };
  } catch {
    return null;
  }
}

function saveToSession(state: ImpersonationState | null) {
  if (state) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } else {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

export const ImpersonationProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useState<ImpersonationState | null>(loadFromSession);

  const startImpersonation = useCallback(
    (userId: string, displayName: string) => {
      const next = { userId, displayName, kind: "admin" as const };
      saveToSession(next);
      setState(next);
    },
    [],
  );

  const startTestSession = useCallback(
    (
      userId: string,
      displayName: string,
      returnTo: string,
      simulatedDate?: string | null,
    ) => {
      const next = {
        userId,
        displayName,
        kind: "test" as const,
        returnTo,
        simulatedDate: simulatedDate || undefined,
      };
      saveToSession(next);
      setState(next);
    },
    [],
  );

  const stopImpersonation = useCallback(() => {
    saveToSession(null);
    setState(null);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedUserId: state?.userId ?? null,
        impersonatedDisplayName: state?.displayName ?? null,
        impersonationKind: state?.kind ?? null,
        impersonationReturnTo: state?.returnTo ?? null,
        impersonationSimulatedDate: state?.simulatedDate ?? null,
        startImpersonation,
        startTestSession,
        stopImpersonation,
        isImpersonating: state !== null,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => useContext(ImpersonationContext);
